/**
 * PODetail.tsx — PO detail view + AP reconciliation panel  (Phase 65 → Phase 68 styling)
 *
 * Route: /purchase-orders/:id
 *
 * Accessible by: all roles that can view POs (role-gates specific action buttons)
 *
 * Sections:
 *  1. Navy header bar (consistent with InvoiceList / PurchaseOrders)
 *  2. Status banner + amounts
 *  3. Summary sidebar (vendor, job, approvals, dates)
 *  4. Line items table
 *  5. Matched invoices panel (AP staff: match / unmatch)
 *  6. PO history / audit log
 *  7. Action buttons: Submit / Approve / Senior Approve / Decline / Issue / Close
 *
 * Uses inline CSSProperties (no Tailwind) — matches portal design system.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getWinTeamEnvironment, getBranding } from '../api';
import {
  getPO,
  submitPO,
  approvePO,
  seniorApprovePO,
  declinePO,
  issuePO,
  closePO,
  matchInvoiceToPO,
  unmatchInvoiceFromPO,
  getPOPdfUrl,
  printPO,
} from '../api/poApi';
import {
  PO_STATUS_LABELS,
  PO_AP_ROLES,
  PO_APPROVER_ROLES,
  PO_REQUESTOR_ROLES,
  PO_SENIOR_APPROVER_ROLES,
  type PurchaseOrder,
  type POInvoiceMatch,
  type POStatus,
} from '../types/poTypes';

/* ── Inline status badge colours ─────────────────────────────── */
const STATUS_BADGE_STYLES: Record<POStatus, { bg: string; color: string }> = {
  DRAFT:                   { bg: '#f0f2f5', color: '#555' },
  PENDING_APPROVAL:        { bg: '#fff8e1', color: '#b8860b' },
  PENDING_SENIOR_APPROVAL: { bg: '#fff3e0', color: '#e65100' },
  APPROVED:                { bg: '#e3f2fd', color: '#1565c0' },
  ISSUED:                  { bg: '#e8f5e9', color: '#2d7a2d' },
  PARTIALLY_INVOICED:      { bg: '#e0f2f1', color: '#00695c' },
  CLOSED:                  { bg: '#f5f5f5', color: '#757575' },
  DECLINED:                { bg: '#fdecea', color: '#c0392b' },
};

/* ── Helpers ─────────────────────────────────────────────────── */
function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function fmtDatetime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function fmtDateShort(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

/* ── Shared style constants ──────────────────────────────────── */
const card: React.CSSProperties = {
  backgroundColor: 'white', borderRadius: '8px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden',
};
const sectionTitle: React.CSSProperties = {
  margin: 0, fontSize: '15px', fontWeight: 700, color: '#1a202c',
};
const sectionHeader: React.CSSProperties = {
  padding: '16px 20px', borderBottom: '1px solid #edf2f7',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 16px', fontSize: '11px',
  color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px',
  fontWeight: 600, borderBottom: '1px solid #edf2f7', backgroundColor: '#fafbfc',
};
const tdStyle: React.CSSProperties = {
  padding: '10px 16px', fontSize: '13px', color: '#333',
  borderBottom: '1px solid #f0f2f5',
};
const btnPrimary: React.CSSProperties = {
  padding: '8px 18px', backgroundColor: '#00386C', color: 'white',
  border: 'none', borderRadius: '6px', cursor: 'pointer',
  fontSize: '13px', fontWeight: 600,
};
const btnOutline: React.CSSProperties = {
  padding: '8px 18px', backgroundColor: 'white',
  border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer',
  fontSize: '13px', fontWeight: 600, color: '#333',
};
const btnGreen: React.CSSProperties = {
  ...btnPrimary, backgroundColor: '#2d7a2d',
};
const btnRedOutline: React.CSSProperties = {
  ...btnOutline, border: '2px solid #e74c3c', color: '#c0392b',
};
const btnDisabled: React.CSSProperties = { opacity: 0.5, pointerEvents: 'none' as const };
const labelStyle: React.CSSProperties = {
  fontSize: '11px', color: '#999', textTransform: 'uppercase',
  letterSpacing: '0.5px', margin: '0 0 2px',
};
const valueStyle: React.CSSProperties = {
  fontSize: '13px', color: '#333', margin: '0 0 12px',
};

/* ── Modal overlay ───────────────────────────────────────────── */
const modalOverlay: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px',
};
const modalCard: React.CSSProperties = {
  backgroundColor: 'white', borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)', width: '100%',
  maxWidth: '440px', padding: '24px',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid #ddd',
  borderRadius: '6px', fontSize: '13px', outline: 'none',
  boxSizing: 'border-box',
};

/* ═══════════════════════════════════════════════════════════════ */
const PODetail: React.FC = () => {
  const navigate    = useNavigate();
  const { id }      = useParams<{ id: string }>();
  const { user, logout, isAdmin, isPowerUser } = useAuth();

  const [po, setPo]               = useState<PurchaseOrder | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError]     = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Decline modal
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason]       = useState('');

  // Approve modal (threshold routing)
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [seniorEmail, setSeniorEmail]           = useState('');

  // Match invoice modal
  const [showMatchModal, setShowMatchModal]     = useState(false);
  const [matchInvoiceId, setMatchInvoiceId]     = useState('');
  const [matchAmount, setMatchAmount]           = useState('');
  const [matchNotes, setMatchNotes]             = useState('');

  // Branding + environment
  const [reverseLogoUrl, setReverseLogoUrl] = useState('');
  const [wtEnvironment, setWtEnvironment]   = useState<string | null>(null);
  const [wtDbName, setWtDbName]             = useState<string | null>(null);

  const canCreate    = PO_REQUESTOR_ROLES.includes(user?.role ?? '');
  const canAP        = PO_AP_ROLES.includes(user?.role ?? '');
  const canApprove   = PO_APPROVER_ROLES.includes(user?.role ?? '');
  const canSrApprove = PO_SENIOR_APPROVER_ROLES.includes(user?.role ?? '');

  useEffect(() => {
    getBranding().then(b => {
      if (b.reverse_logo_url) setReverseLogoUrl(b.reverse_logo_url);
      if (b.portal_tab) document.title = b.portal_tab;
    });
    getWinTeamEnvironment()
      .then(r => { setWtEnvironment(r.environment); setWtDbName(r.db_name); })
      .catch(() => {});
  }, []);

  const loadPO = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getPO(parseInt(id, 10));
      setPo(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadPO(); }, [loadPO]);

  async function runAction(fn: () => Promise<void>, successMsg: string) {
    setActionLoading(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      await fn();
      setActionSuccess(successMsg);
      await loadPO();
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  }

  /* ── Loading / error states ──────────────────────────────────── */
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#999', fontSize: '14px' }}>Loading purchase order…</span>
      </div>
    );
  }
  if (error || !po) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
        <span style={{ color: '#c0392b', fontSize: '14px' }}>{error || 'PO not found'}</span>
        <button onClick={() => navigate('/purchase-orders')} style={{ ...btnOutline, fontSize: '13px' }}>
          ← Back to Purchase Orders
        </button>
      </div>
    );
  }

  const lines    = po.lines    || [];
  const history  = po.history  || [];
  const matches  = (po.matches || []).filter((m: POInvoiceMatch) => m.active);
  const invoicedPct = po.total_amount > 0
    ? Math.min(100, Math.round((po.invoiced_amount / po.total_amount) * 100))
    : 0;
  const badge = STATUS_BADGE_STYLES[po.status] || { bg: '#f0f2f5', color: '#555' };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5', fontFamily: 'Segoe UI, sans-serif' }}>

      {/* ── Navy Header ───────────────────────────────────────────── */}
      <div style={{ backgroundColor: '#00386C', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '56px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {reverseLogoUrl
            ? <img src={reverseLogoUrl} alt="Logo" style={{ height: '28px', objectFit: 'contain', display: 'block' }} />
            : <span style={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>AP Portal</span>
          }
          {isAdmin && (
            <span style={{ backgroundColor: '#b8860b', color: 'white', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px' }}>
              ADMIN
            </span>
          )}
          {wtEnvironment && (
            <span style={{
              backgroundColor: wtEnvironment === 'PRODUCTION' ? '#c0392b' : '#1a7a4a',
              color: 'white', fontSize: '11px', fontWeight: 700,
              padding: '2px 8px', borderRadius: '10px', letterSpacing: '0.5px',
            }}>
              {wtEnvironment === 'PRODUCTION' ? '⚠ PRODUCTION' : 'TRAINING'}{wtDbName ? ` — ${wtDbName}` : ''}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => navigate('/invoices')} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '4px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            Invoices
          </button>
          <button onClick={() => navigate('/purchase-orders')} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '4px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            Purchase Orders
          </button>
          {isPowerUser && (
            <button onClick={() => navigate('/credit-card-upload')} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '4px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
              Credit Card Upload
            </button>
          )}
          {isAdmin && (
            <button onClick={() => navigate('/admin')} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '4px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
              Admin Panel
            </button>
          )}
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>{user?.display_name}</span>
          <button onClick={logout} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
            Sign out
          </button>
        </div>
      </div>

      {/* ── Main content area ─────────────────────────────────────── */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>

        {/* Back link */}
        <button
          onClick={() => navigate('/purchase-orders')}
          style={{ background: 'none', border: 'none', color: '#718096', fontSize: '13px', cursor: 'pointer', padding: 0, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          ← Back to Purchase Orders
        </button>

        {/* ── Status Banner Card ────────────────────────────────────── */}
        <div style={{ ...card, padding: '24px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            {/* Left: PO number + badge + vendor */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px', fontWeight: 700, color: '#1a202c', fontFamily: 'monospace' }}>
                  {po.po_number}
                </span>
                <span style={{
                  display: 'inline-block', padding: '3px 12px', borderRadius: '12px',
                  fontSize: '12px', fontWeight: 700,
                  backgroundColor: badge.bg, color: badge.color,
                }}>
                  {PO_STATUS_LABELS[po.status]}
                </span>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: '14px', color: '#718096' }}>
                {po.vendor_name} · Job {po.job_number}
                {po.job_name && ` — ${po.job_name}`}
              </p>
            </div>

            {/* Right: total + invoiced */}
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#1a202c' }}>
                {fmtCurrency(po.total_amount)}
              </div>
              {po.invoiced_amount > 0 && (
                <div style={{ fontSize: '13px', color: '#999', marginTop: '2px' }}>
                  {fmtCurrency(po.invoiced_amount)} invoiced ({invoicedPct}%)
                </div>
              )}
              {po.total_amount > 0 && po.invoiced_amount > 0 && (
                <div style={{ width: '160px', height: '6px', backgroundColor: '#edf2f7', borderRadius: '3px', overflow: 'hidden', marginTop: '8px', marginLeft: 'auto' }}>
                  <div style={{ width: `${invoicedPct}%`, height: '100%', backgroundColor: '#2d7a2d', borderRadius: '3px' }} />
                </div>
              )}
            </div>
          </div>

          {/* Action feedback */}
          {actionSuccess && (
            <div style={{ marginTop: '16px', padding: '10px 16px', backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: '6px', color: '#2d7a2d', fontSize: '13px' }}>
              {actionSuccess}
            </div>
          )}
          {actionError && (
            <div style={{ marginTop: '16px', padding: '10px 16px', backgroundColor: '#fdecea', border: '1px solid #f5c6cb', borderRadius: '6px', color: '#c0392b', fontSize: '13px' }}>
              {actionError}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {po.status === 'DRAFT' && canCreate && (
              <>
                <button onClick={() => navigate(`/purchase-orders/${po.id}/edit`)} style={btnOutline}>
                  Edit PO
                </button>
                <button
                  onClick={() => runAction(() => submitPO(po.id).then(() => {}), 'PO submitted for approval!')}
                  disabled={actionLoading}
                  style={{ ...btnPrimary, ...(actionLoading ? btnDisabled : {}) }}
                >
                  Submit for Approval
                </button>
              </>
            )}
            {po.status === 'PENDING_APPROVAL' && canApprove && (
              <>
                <button
                  onClick={() => setShowApproveModal(true)}
                  disabled={actionLoading}
                  style={{ ...btnGreen, ...(actionLoading ? btnDisabled : {}) }}
                >
                  Approve
                </button>
                <button
                  onClick={() => setShowDeclineModal(true)}
                  disabled={actionLoading}
                  style={{ ...btnRedOutline, ...(actionLoading ? btnDisabled : {}) }}
                >
                  Decline
                </button>
              </>
            )}
            {po.status === 'PENDING_SENIOR_APPROVAL' && canSrApprove && (
              <>
                <button
                  onClick={() => runAction(() => seniorApprovePO(po.id).then(() => {}), 'Senior approval granted!')}
                  disabled={actionLoading}
                  style={{ ...btnGreen, ...(actionLoading ? btnDisabled : {}) }}
                >
                  Senior Approve
                </button>
                <button
                  onClick={() => setShowDeclineModal(true)}
                  disabled={actionLoading}
                  style={{ ...btnRedOutline, ...(actionLoading ? btnDisabled : {}) }}
                >
                  Decline
                </button>
              </>
            )}
            {po.status === 'APPROVED' && canAP && (
              <button
                onClick={() => runAction(() => issuePO(po.id).then(() => {}), 'PO issued to vendor!')}
                disabled={actionLoading}
                style={{ ...btnPrimary, ...(actionLoading ? btnDisabled : {}) }}
              >
                Issue PO to Vendor
              </button>
            )}
            {(po.status === 'ISSUED' || po.status === 'PARTIALLY_INVOICED') && canAP && (
              <>
                <button onClick={() => setShowMatchModal(true)} style={{ ...btnOutline, border: '1px solid #1565c0', color: '#1565c0' }}>
                  Match Invoice
                </button>
                <button
                  onClick={() => runAction(() => closePO(po.id).then(() => {}), 'PO closed.')}
                  disabled={actionLoading}
                  style={{ ...btnOutline, ...(actionLoading ? btnDisabled : {}) }}
                >
                  Close PO
                </button>
              </>
            )}
            {po.po_pdf_blob_url ? (
              /* ISSUED / PARTIALLY_INVOICED / CLOSED — use stored blob PDF */
              <button
                onClick={async () => {
                  try {
                    const url = await getPOPdfUrl(po.id);
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } catch {
                    alert('Could not load PDF. Please try again.');
                  }
                }}
                style={btnOutline}
              >
                View PDF ↗
              </button>
            ) : (
              /* DRAFT / PENDING / APPROVED — generate on-the-fly, no status change */
              <button
                onClick={async () => {
                  try {
                    const url = await printPO(po.id);
                    const win = window.open(url, '_blank', 'noopener,noreferrer');
                    // Revoke the blob URL after a short delay so the new tab
                    // has time to start loading the PDF.
                    setTimeout(() => URL.revokeObjectURL(url), 30_000);
                    if (!win) alert('Pop-up blocked — please allow pop-ups for this site.');
                  } catch {
                    alert('Could not generate PDF. Please try again.');
                  }
                }}
                style={btnOutline}
              >
                Print PO ↗
              </button>
            )}
          </div>
        </div>

        {/* ── Two-column layout ────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', alignItems: 'start' }}>

          {/* ── LEFT: Line items + Matches + History ────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

            {/* Line Items */}
            <div style={card}>
              <div style={sectionHeader}>
                <h2 style={sectionTitle}>Line Items</h2>
                <span style={{ fontSize: '12px', color: '#999' }}>{lines.length} item{lines.length !== 1 ? 's' : ''}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: '40px' }}>#</th>
                    <th style={thStyle}>Description</th>
                    <th style={{ ...thStyle, width: '80px' }}>GL</th>
                    <th style={{ ...thStyle, width: '60px', textAlign: 'right' }}>Qty</th>
                    <th style={{ ...thStyle, width: '90px', textAlign: 'right' }}>Unit $</th>
                    <th style={{ ...thStyle, width: '100px', textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(l => (
                    <tr key={l.id}>
                      <td style={{ ...tdStyle, color: '#999' }}>{l.line_number}</td>
                      <td style={tdStyle}>
                        {l.description}
                        {l.job_number && po.job_number !== l.job_number && (
                          <div style={{ fontSize: '11px', color: '#999' }}>Job {l.job_number}</div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#666' }}>{l.gl_account || '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#666' }}>
                        {l.entry_mode === 'FLAT_TOTAL'
                          ? <span style={{ fontSize: '11px', color: '#999', fontStyle: 'italic' }}>flat</span>
                          : l.quantity}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#666' }}>{fmtCurrency(l.unit_price)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmtCurrency(l.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: '#fafbfc' }}>
                    <td colSpan={5} style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#666', borderBottom: 'none' }}>Total</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, fontSize: '15px', color: '#1a202c', borderBottom: 'none' }}>{fmtCurrency(po.total_amount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Matched Invoices */}
            <div style={card}>
              <div style={sectionHeader}>
                <h2 style={sectionTitle}>Matched Invoices</h2>
                <span style={{ fontSize: '12px', color: '#999' }}>{matches.length} match{matches.length !== 1 ? 'es' : ''}</span>
              </div>
              {matches.length === 0 ? (
                <div style={{ padding: '20px', fontSize: '13px', color: '#999' }}>No invoices matched yet.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Invoice</th>
                      <th style={thStyle}>Matched</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                      <th style={thStyle}>Type</th>
                      {canAP && <th style={{ ...thStyle, width: '70px' }} />}
                    </tr>
                  </thead>
                  <tbody>
                    {matches.map((m: POInvoiceMatch) => (
                      <tr key={m.id}>
                        <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#1565c0', fontWeight: 600 }}>
                          {m.invoice_number || m.invoice_id}
                        </td>
                        <td style={{ ...tdStyle, color: '#999', fontSize: '12px' }}>{fmtDatetime(m.matched_at)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmtCurrency(m.matched_amount)}</td>
                        <td style={tdStyle}>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
                            fontSize: '11px', fontWeight: 600,
                            backgroundColor: m.match_type === 'AUTO' ? '#e3f2fd' : '#f0f2f5',
                            color: m.match_type === 'AUTO' ? '#1565c0' : '#666',
                          }}>
                            {m.match_type}
                          </span>
                        </td>
                        {canAP && (
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <button
                              onClick={() => runAction(
                                () => unmatchInvoiceFromPO(po.id, { match_id: m.id }).then(() => {}),
                                'Invoice unmatched.',
                              )}
                              style={{ background: 'none', border: 'none', color: '#c0392b', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}
                            >
                              Unmatch
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Audit History */}
            <div style={card}>
              <div style={sectionHeader}>
                <h2 style={sectionTitle}>History</h2>
              </div>
              {history.length === 0 ? (
                <div style={{ padding: '20px', fontSize: '13px', color: '#999' }}>No history yet.</div>
              ) : (
                <div>
                  {[...history].reverse().map(h => (
                    <div key={h.id} style={{ padding: '12px 20px', borderBottom: '1px solid #f0f2f5', display: 'flex', gap: '16px' }}>
                      <div style={{ width: '90px', flexShrink: 0, fontSize: '12px', color: '#999', paddingTop: '2px' }}>
                        {new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {h.event_type.replace(/_/g, ' ')}
                          </span>
                          {h.new_status && (() => {
                            const hBadge = STATUS_BADGE_STYLES[h.new_status as POStatus];
                            return hBadge ? (
                              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 600, backgroundColor: hBadge.bg, color: hBadge.color }}>
                                {PO_STATUS_LABELS[h.new_status as POStatus]}
                              </span>
                            ) : null;
                          })()}
                        </div>
                        {h.note && (
                          <div style={{ fontSize: '13px', color: '#555', marginTop: '2px' }}>{h.note}</div>
                        )}
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
                          {h.actor_name || h.actor_email}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── RIGHT: Details sidebar ──────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

            {/* Details card */}
            <div style={{ ...card, padding: '20px' }}>
              <h2 style={{ ...sectionTitle, marginBottom: '16px' }}>Details</h2>

              <p style={labelStyle}>Requested by</p>
              <p style={valueStyle}>
                {po.requested_by_name || po.requested_by_email}
                <br />
                <span style={{ fontSize: '12px', color: '#999' }}>{fmtDatetime(po.requested_at)}</span>
              </p>

              {po.approver_email && (
                <>
                  <p style={labelStyle}>Approver</p>
                  <p style={valueStyle}>
                    {po.approver_email}
                    {po.approved_at && (
                      <><br /><span style={{ fontSize: '12px', color: '#2d7a2d' }}>Approved {fmtDatetime(po.approved_at)}</span></>
                    )}
                  </p>
                </>
              )}

              {po.senior_approver_email && (
                <>
                  <p style={labelStyle}>Senior Approver</p>
                  <p style={valueStyle}>
                    {po.senior_approver_email}
                    {po.senior_approved_at && (
                      <><br /><span style={{ fontSize: '12px', color: '#2d7a2d' }}>Approved {fmtDatetime(po.senior_approved_at)}</span></>
                    )}
                  </p>
                </>
              )}

              {po.declined_by_email && (
                <>
                  <p style={labelStyle}>Declined by</p>
                  <p style={{ ...valueStyle, color: '#c0392b' }}>
                    {po.declined_by_email}
                    {po.decline_reason && (
                      <><br /><span style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>"{po.decline_reason}"</span></>
                    )}
                  </p>
                </>
              )}

              {po.issued_at && (
                <>
                  <p style={labelStyle}>Issued</p>
                  <p style={valueStyle}>
                    {fmtDatetime(po.issued_at)}
                    <br /><span style={{ fontSize: '12px', color: '#999' }}>by {po.issued_by_email}</span>
                  </p>
                </>
              )}

              {po.po_type && (
                <>
                  <p style={labelStyle}>PO Type</p>
                  <p style={valueStyle}>{po.po_type === 'BLANKET' ? 'Blanket' : 'Spot Buy'}</p>
                </>
              )}

              {po.po_date && (
                <>
                  <p style={labelStyle}>PO Date</p>
                  <p style={valueStyle}>{fmtDateShort(po.po_date)}</p>
                </>
              )}

              {po.required_by_date && (
                <>
                  <p style={labelStyle}>Required By</p>
                  <p style={valueStyle}>{fmtDateShort(po.required_by_date)}</p>
                </>
              )}

              {po.payment_terms && (
                <>
                  <p style={labelStyle}>Payment Terms</p>
                  <p style={valueStyle}>{po.payment_terms}</p>
                </>
              )}

              {po.description && (
                <>
                  <p style={labelStyle}>Description</p>
                  <p style={valueStyle}>{po.description}</p>
                </>
              )}

              {po.notes_vendor && (
                <>
                  <p style={labelStyle}>Vendor Notes</p>
                  <p style={{ ...valueStyle, color: '#555' }}>{po.notes_vendor}</p>
                </>
              )}

              {po.notes && (
                <>
                  <p style={labelStyle}>Internal Notes</p>
                  <p style={{ ...valueStyle, color: '#666', fontStyle: 'italic' }}>{po.notes}</p>
                </>
              )}

              <p style={labelStyle}>Created</p>
              <p style={{ ...valueStyle, marginBottom: 0, fontSize: '12px', color: '#999' }}>{fmtDatetime(po.created_at)}</p>
            </div>

            {/* Ship-to card */}
            {(po.ship_to_address || po.ship_to_city) && (
              <div style={{ ...card, padding: '20px' }}>
                <h2 style={{ ...sectionTitle, marginBottom: '12px' }}>Ship-To</h2>
                <div style={{ fontSize: '13px', color: '#333', lineHeight: '1.6' }}>
                  {po.ship_to_address && <div>{po.ship_to_address}</div>}
                  {(po.ship_to_city || po.ship_to_state || po.ship_to_zip) && (
                    <div>
                      {[po.ship_to_city, po.ship_to_state].filter(Boolean).join(', ')}
                      {po.ship_to_zip && ` ${po.ship_to_zip}`}
                    </div>
                  )}
                  {po.ship_to_contact_name && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                      Contact: {po.ship_to_contact_name}
                      {po.ship_to_contact_phone && ` · ${po.ship_to_contact_phone}`}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Decline modal ──────────────────────────────────────────── */}
      {showDeclineModal && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#1a202c' }}>Decline PO</h3>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#718096' }}>
              Please provide a reason. The requestor will see this message.
            </p>
            <textarea
              rows={3}
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              style={{ ...inputStyle, resize: 'none' }}
              placeholder="Reason for declining…"
              autoFocus
            />
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button onClick={() => { setShowDeclineModal(false); setDeclineReason(''); }} style={{ ...btnOutline, flex: 1 }}>
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDeclineModal(false);
                  runAction(() => declinePO(po.id, { reason: declineReason }).then(() => {}), 'PO declined.');
                  setDeclineReason('');
                }}
                disabled={!declineReason.trim()}
                style={{ ...btnPrimary, flex: 1, backgroundColor: '#c0392b', ...(!declineReason.trim() ? btnDisabled : {}) }}
              >
                Confirm Decline
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Approve modal (senior routing) ─────────────────────────── */}
      {showApproveModal && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#1a202c' }}>Approve PO</h3>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#718096' }}>
              If this PO requires senior approval (based on dollar threshold), enter the senior approver's email. Leave blank if you're approving directly.
            </p>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>
              Senior Approver Email <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              type="email"
              value={seniorEmail}
              onChange={e => setSeniorEmail(e.target.value)}
              style={inputStyle}
              placeholder="senior@powerlinkonline.com"
            />
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button onClick={() => { setShowApproveModal(false); setSeniorEmail(''); }} style={{ ...btnOutline, flex: 1 }}>
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  runAction(
                    () => approvePO(po.id, { senior_approver_email: seniorEmail.trim() || undefined }).then(() => {}),
                    'PO approved!',
                  );
                  setSeniorEmail('');
                }}
                style={{ ...btnGreen, flex: 1 }}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Match invoice modal ────────────────────────────────────── */}
      {showMatchModal && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 700, color: '#1a202c' }}>Match Invoice to PO</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>
                  Invoice ID <span style={{ color: '#c0392b' }}>*</span>
                </label>
                <input
                  type="number"
                  value={matchInvoiceId}
                  onChange={e => setMatchInvoiceId(e.target.value)}
                  style={inputStyle}
                  placeholder="Invoice database ID"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>
                  Matched Amount <span style={{ color: '#c0392b' }}>*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={matchAmount}
                  onChange={e => setMatchAmount(e.target.value)}
                  style={inputStyle}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>
                  Notes <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span>
                </label>
                <input
                  type="text"
                  value={matchNotes}
                  onChange={e => setMatchNotes(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button
                onClick={() => { setShowMatchModal(false); setMatchInvoiceId(''); setMatchAmount(''); setMatchNotes(''); }}
                style={{ ...btnOutline, flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!matchInvoiceId || !matchAmount) return;
                  setShowMatchModal(false);
                  runAction(
                    () => matchInvoiceToPO(po.id, {
                      invoice_id: parseInt(matchInvoiceId, 10),
                      matched_amount: parseFloat(matchAmount),
                      notes: matchNotes || undefined,
                    }).then(() => {}),
                    'Invoice matched to PO.',
                  );
                  setMatchInvoiceId(''); setMatchAmount(''); setMatchNotes('');
                }}
                disabled={!matchInvoiceId || !matchAmount}
                style={{ ...btnPrimary, flex: 1, ...(!matchInvoiceId || !matchAmount ? btnDisabled : {}) }}
              >
                Match Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PODetail;
